import { useMemo } from 'react';
import axios from 'axios';

import {
    DataProvider,
    DataProviderResult,
    DataSection,
    StaticDataFetcherItem,
} from '@hh.ru/magritte-common-data-provider/types';
import { debounce } from '@hh.ru/magritte-common-func-utils';
import { match } from '@hh.ru/magritte-common-fuzzy-search';

type CancelCallback = (callback: VoidFunction) => void;

export interface FetcherResultProducer<R = never, I = undefined> {
    (data: I | null, query: string): DataProviderResult<R>;
}
export interface AsyncDataFetcher<R = never, T = DataProviderResult<R>> {
    (url: string, onCancel: CancelCallback): Promise<T | null>;
}

export const decorateWithQueryTransformer =
    <K, T>(fetcher: AsyncDataFetcher<K, T>, decorator: (query: string) => string): AsyncDataFetcher<K, T> =>
    (query, onCancel) =>
        fetcher(decorator(query), onCancel);

export const decorateWithResultProducer =
    <T, F, K = never>(
        fetcher: AsyncDataFetcher<F, T>,
        resultProducer: FetcherResultProducer<K, T>
    ): AsyncDataFetcher<K> =>
    async (query, onCancel) =>
        resultProducer(await fetcher(query, onCancel), query);

export const combineProviders = <T>(providers: Array<DataProvider<T>>): DataProvider<T> => {
    const provider = (query: string) => {
        const provider = providers.find((provider) => query.length >= provider.minCharsCount) || providers[0];
        return provider(query);
    };

    provider.minCharsCount = Math.min(...providers.map((provider) => provider.minCharsCount));
    return provider;
};

interface ResolverCallback<T> {
    (arg: { resolve: (result: T | null) => void; reject: (err?: unknown) => void }): VoidFunction | void;
}

const createCancelablePromise = <T>(callback: ResolverCallback<T>) => {
    let isCanceled = false;
    let cancelHandler: VoidFunction | void;

    const promise = new Promise<T | null>((resolve, reject) => {
        cancelHandler = callback({
            resolve: (data: T | null) => {
                !isCanceled && resolve(data);
            },
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            reject: (err: unknown) => !isCanceled && reject(err),
        });
    }) as Promise<T> & { cancel: VoidFunction };

    promise.cancel = () => {
        isCanceled = true;
        cancelHandler?.();
    };
    return promise;
};

export const defaultFetcher = <T>(url: string, onCancel: CancelCallback): Promise<T | null> => {
    const controller = new AbortController();
    onCancel(() => controller.abort());

    return axios
        .get<T>(url, { signal: controller.signal })
        .then((response) => response.data)
        .catch((error) => {
            if (!axios.isCancel(error)) {
                throw error;
            }
            return null;
        });
};

export class DataProviderError extends Error {}

export const createDataProvider = <K>({
    fetcher,
    debounceTimeout = 300,
    minCharsCount = 3,
}: {
    fetcher: AsyncDataFetcher<K>;
    debounceTimeout?: number;
    minCharsCount?: number;
}): DataProvider<K> => {
    const makeQuery = debounce(
        ({
            query,
            resolve,
            reject,
            cancelation,
        }: Parameters<ResolverCallback<DataProviderResult<K>>>[0] & {
            query: string;
            cancelation: { isCanceled: boolean; onCancel: CancelCallback };
        }) => {
            if (query.length < minCharsCount || cancelation.isCanceled) {
                reject(new DataProviderError());
                return;
            }
            fetcher(query, cancelation.onCancel).then(resolve, reject);
        },
        debounceTimeout
    );

    const provider = (query: string) =>
        createCancelablePromise<DataProviderResult<K>>(({ resolve, reject }) => {
            const cancelCallbacks: VoidFunction[] = [];
            const cancelation = {
                isCanceled: false,
                onCancel: (callback: VoidFunction) => cancelCallbacks.push(callback),
            };
            makeQuery({ query, resolve, reject, cancelation });
            return () => {
                cancelation.isCanceled = true;
                cancelCallbacks.forEach((callback) => callback());
            };
        });

    provider.minCharsCount = minCharsCount;
    return provider;
};

export const createStaticDataFetcher =
    <T extends StaticDataFetcherItem>(data: Array<T>, searchField?: keyof T): AsyncDataFetcher<never, T[]> =>
    (query) =>
        Promise.resolve(
            data.filter((item) => {
                if (searchField) {
                    return match(query, String(item[searchField]));
                }
                const { text, value } = item;
                return text ? match(query, text) : match(query, value);
            })
        );

export const createStaticDataProvider = <T extends StaticDataFetcherItem>(
    data: Array<T>,
    title?: string
): DataProvider<T> =>
    createDataProvider({
        minCharsCount: 0,
        debounceTimeout: 0,
        fetcher: decorateWithResultProducer(createStaticDataFetcher(data), (fetcherData) => {
            const sections = [
                {
                    type: 'cells',
                    items:
                        fetcherData?.map((item) => ({
                            value: item.value,
                            data: item,
                            disabled: item.disabled,
                        })) ?? [],
                } as DataSection<T>,
            ];
            if (title) {
                sections.unshift({ type: 'delimiter', text: title });
            }
            return sections;
        }),
    });

export const useStaticDataProvider = <T extends StaticDataFetcherItem>(
    data: Array<T>,
    title?: string
): DataProvider<T> => {
    return useMemo(() => createStaticDataProvider(data, title), [data, title]);
};

export function findStaticDataFetcherItemByValue<DataType extends StaticDataFetcherItem>(
    value: string,
    items: Array<DataType>
): DataType | undefined;

export function findStaticDataFetcherItemByValue<DataType extends StaticDataFetcherItem>(
    value: string[],
    items: Array<DataType>
): DataType[];

export function findStaticDataFetcherItemByValue<DataType extends StaticDataFetcherItem>(
    value: string | string[],
    items: Array<DataType>
): DataType | DataType[] | undefined {
    if (Array.isArray(value)) {
        return items.filter((item) => value.includes(item.value));
    }
    return items.find((item) => item.value === value);
}
